import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PaginationControls, clampPage } from "@/components/pagination-controls";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DrillUser, GeminiUsageResponse } from "./types";
import { OTHER_COLOR, TREND_COLORS } from "./constants";
import { formatDate, formatNum, formatUsd } from "./helpers";

export function GeminiUserDrilldownDialog({
  user,
  hours,
  rangeLabel,
  alertDate,
  onClose,
}: {
  user: DrillUser | null;
  hours: number;
  rangeLabel: string;
  alertDate?: string | null;
  onClose: () => void;
}) {
  const open = user != null;
  const [drillMetric, setDrillMetric] = useState<"cost" | "totalTokens">("cost");
  const [featureFilter, setFeatureFilter] = useState<string | null>(null);
  const [byFeaturePage, setByFeaturePage] = useState(1);
  const [byFeaturePageSize, setByFeaturePageSize] = useState(25);
  const [recentPage, setRecentPage] = useState(1);
  const [recentPageSize, setRecentPageSize] = useState(25);
  useEffect(() => {
    setFeatureFilter(null);
  }, [open, user?.userId]);
  useEffect(() => {
    setByFeaturePage(1);
    setRecentPage(1);
  }, [featureFilter, user?.userId, alertDate, hours]);
  const userIdParam = user == null ? null : user.userId == null ? "anon" : String(user.userId);
  // When opened from a spike alert, focus the query on that UTC calendar day
  // regardless of the panel's current "Last 24h / 7d / 30d" range so the
  // alert's calls are visible by default.
  const dayWindow = (() => {
    if (!alertDate) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(alertDate);
    if (!m) return null;
    const startMs = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return { startMs, endMs: startMs + 24 * 60 * 60 * 1000 };
  })();
  const drillQuery = useQuery<GeminiUsageResponse>({
    queryKey: dayWindow
      ? ["/api/admin/gemini-usage", "day", dayWindow.startMs, userIdParam, featureFilter]
      : ["/api/admin/gemini-usage", hours, userIdParam, featureFilter],
    queryFn: async () => {
      const featureSuffix = featureFilter
        ? `&feature=${encodeURIComponent(featureFilter === "(unknown)" ? "__null__" : featureFilter)}`
        : "";
      const url = dayWindow
        ? `/api/admin/gemini-usage?startMs=${dayWindow.startMs}&endMs=${dayWindow.endMs}&userId=${encodeURIComponent(userIdParam!)}${featureSuffix}`
        : `/api/admin/gemini-usage?hours=${hours}&userId=${encodeURIComponent(userIdParam!)}${featureSuffix}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load user usage");
      return res.json();
    },
    enabled: open && userIdParam != null,
  });

  const heading = user
    ? user.email || user.displayName || (user.userId == null ? "Anonymous" : `User #${user.userId}`)
    : "";
  const effectiveRangeLabel = alertDate
    ? `Spike-alert day · ${alertDate} (UTC)`
    : rangeLabel;

  const featureChart = (() => {
    const series = drillQuery.data?.series;
    if (!series || series.points.length === 0) return null;
    const valueOf = (p: { estimatedCostUsd: number; totalTokens: number }) =>
      drillMetric === "cost" ? p.estimatedCostUsd : p.totalTokens;
    const featureTotals = new Map<string, number>();
    for (const p of series.points) {
      const f = p.feature ?? "(unknown)";
      featureTotals.set(f, (featureTotals.get(f) ?? 0) + valueOf(p));
    }
    const ranked = Array.from(featureTotals.entries())
      .map(([feature, total]) => ({ feature, total }))
      .sort((a, b) => b.total - a.total);
    if (ranked.length === 0) return null;
    const TOP_FEATURES = 6;
    const topKeys = new Set(ranked.slice(0, TOP_FEATURES).map((r) => r.feature));
    const hasOther = ranked.length > TOP_FEATURES;
    const seriesKeys: Array<{ key: string; label: string; color: string }> = ranked
      .slice(0, TOP_FEATURES)
      .map((r, i) => ({ key: r.feature, label: r.feature, color: TREND_COLORS[i % TREND_COLORS.length] }));
    if (hasOther) seriesKeys.push({ key: "__other__", label: "Other", color: OTHER_COLOR });

    const bucketMap = new Map<string, Record<string, number | string>>();
    for (const p of series.points) {
      const row = bucketMap.get(p.bucketStart) ?? { bucketStart: p.bucketStart };
      const f = p.feature ?? "(unknown)";
      const bucketKey = topKeys.has(f) ? f : "__other__";
      row[bucketKey] = (Number(row[bucketKey]) || 0) + valueOf(p);
      bucketMap.set(p.bucketStart, row);
    }
    const chartData = Array.from(bucketMap.values()).sort((a, b) =>
      String(a.bucketStart).localeCompare(String(b.bucketStart)),
    );
    for (const row of chartData) {
      for (const sk of seriesKeys) {
        if (row[sk.key] == null) row[sk.key] = 0;
      }
    }

    const formatBucket = (iso: string) => {
      const d = new Date(iso);
      if (series.bucket === "hour") {
        return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" });
      }
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    };
    const formatValue = (v: number) => (drillMetric === "cost" ? formatUsd(v) : formatNum(v));

    const handleSeriesClick = (key: string, label: string) => {
      if (key === "__other__") return;
      setFeatureFilter((prev) => (prev === label ? null : label));
    };
    return (
      <Card data-testid="card-drilldown-feature-trend">
        <div className="p-3 border-b flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-semibold text-sm">Usage over time by feature</h4>
            <p className="text-xs text-muted-foreground">
              Stacked {drillMetric === "cost" ? "estimated cost" : "total tokens"} per {series.bucket}
              {hasOther ? `, top ${TOP_FEATURES} features plus Other` : ""}.
              {" "}Click a feature to filter.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Metric</Label>
            <Select value={drillMetric} onValueChange={(v) => setDrillMetric(v as "cost" | "totalTokens")}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="select-drilldown-metric">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cost">Est. cost</SelectItem>
                <SelectItem value="totalTokens">Total tokens</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="p-3">
          <div className="h-64 w-full" data-testid="chart-drilldown-feature-trend">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis
                  dataKey="bucketStart"
                  tickFormatter={(v) => formatBucket(String(v))}
                  minTickGap={24}
                  fontSize={11}
                />
                <YAxis tickFormatter={(v) => formatValue(Number(v))} fontSize={11} width={70} />
                <RechartsTooltip
                  labelFormatter={(label) => formatBucket(String(label))}
                  formatter={(value, name) => [formatValue(Number(value)), String(name)]}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, cursor: "pointer" }}
                  onClick={(payload: any) => {
                    const key = String(payload?.dataKey ?? "");
                    const label = String(payload?.value ?? key);
                    handleSeriesClick(key, label);
                  }}
                />
                {seriesKeys.map((sk) => (
                  <Area
                    key={sk.key}
                    type="monotone"
                    dataKey={sk.key}
                    name={sk.label}
                    stackId="1"
                    stroke={sk.color}
                    fill={sk.color}
                    fillOpacity={featureFilter && sk.key !== "__other__" && sk.label === featureFilter ? 0.85 : 0.5}
                    style={{ cursor: sk.key === "__other__" ? "default" : "pointer" }}
                    onClick={() => handleSeriesClick(sk.key, sk.label)}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>
    );
  })();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-gemini-user-drilldown"
      >
        <DialogHeader>
          <DialogTitle data-testid="text-drilldown-title">Gemini usage — {heading}</DialogTitle>
          <p className="text-xs text-muted-foreground" data-testid="text-drilldown-range">{effectiveRangeLabel}{user?.displayName && user?.email ? ` · ${user.displayName}` : ""}</p>
        </DialogHeader>
        {drillQuery.isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : drillQuery.isError ? (
          <div className="text-sm text-destructive" data-testid="text-drilldown-error">
            Failed to load this user's calls.
          </div>
        ) : drillQuery.data ? (
          <div className="space-y-4">
            {featureFilter && (
              <div
                className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs"
                data-testid="banner-drilldown-feature-filter"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-muted-foreground">Filtered to feature:</span>
                  <Badge variant="secondary" data-testid="badge-drilldown-feature-filter">{featureFilter}</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setFeatureFilter(null)}
                  data-testid="button-clear-drilldown-feature-filter"
                >
                  Clear filter
                </Button>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="p-3">
                <div className="text-xs text-muted-foreground">Calls</div>
                <div className="text-lg font-semibold" data-testid="text-drilldown-calls">
                  {formatNum(drillQuery.data.totals.calls)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatNum(drillQuery.data.totals.failures)} failed
                </div>
              </Card>
              <Card className="p-3">
                <div className="text-xs text-muted-foreground">Input tokens</div>
                <div className="text-lg font-semibold">{formatNum(drillQuery.data.totals.inputTokens)}</div>
              </Card>
              <Card className="p-3">
                <div className="text-xs text-muted-foreground">Output tokens</div>
                <div className="text-lg font-semibold">{formatNum(drillQuery.data.totals.outputTokens)}</div>
              </Card>
              <Card className="p-3">
                <div className="text-xs text-muted-foreground">Estimated cost</div>
                <div className="text-lg font-semibold" data-testid="text-drilldown-cost">
                  {formatUsd(drillQuery.data.totals.estimatedCostUsd)}
                </div>
              </Card>
            </div>

            {featureChart}

            <Card>
              <div className="p-3 border-b">
                <h4 className="font-semibold text-sm">By feature</h4>
              </div>
              {drillQuery.data.byFeature.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground" data-testid="text-drilldown-no-features">
                  No calls in this window.
                </div>
              ) : (() => {
                const total = drillQuery.data.byFeature.length;
                const safePage = clampPage(byFeaturePage, total, byFeaturePageSize);
                const start = (safePage - 1) * byFeaturePageSize;
                const pageRows = drillQuery.data.byFeature.slice(start, start + byFeaturePageSize);
                return (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Feature</TableHead>
                          <TableHead>Model</TableHead>
                          <TableHead className="text-right">Calls</TableHead>
                          <TableHead className="text-right">Failures</TableHead>
                          <TableHead className="text-right">Tokens</TableHead>
                          <TableHead className="text-right">Est. cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pageRows.map((row, idx) => (
                          <TableRow key={`${row.feature}-${row.model}`} data-testid={`row-drilldown-feature-${start + idx}`}>
                            <TableCell className="font-medium">{row.feature}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{row.model}</TableCell>
                            <TableCell className="text-right">{formatNum(row.calls)}</TableCell>
                            <TableCell className="text-right">
                              {row.failures > 0
                                ? <Badge variant="destructive">{formatNum(row.failures)}</Badge>
                                : <span className="text-muted-foreground">0</span>}
                            </TableCell>
                            <TableCell className="text-right">{formatNum(row.totalTokens)}</TableCell>
                            <TableCell className="text-right font-medium">{formatUsd(row.estimatedCostUsd)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="p-3 border-t">
                      <PaginationControls
                        page={safePage}
                        pageSize={byFeaturePageSize}
                        total={total}
                        onPageChange={setByFeaturePage}
                        onPageSizeChange={setByFeaturePageSize}
                        testIdPrefix="drilldown-by-feature"
                        itemLabel="features"
                      />
                    </div>
                  </>
                );
              })()}
            </Card>

            <Card>
              <div className="p-3 border-b">
                <h4 className="font-semibold text-sm">Recent calls</h4>
                <p className="text-xs text-muted-foreground">
                  Most recent {drillQuery.data.recent.length} calls in this window.
                </p>
              </div>
              {drillQuery.data.recent.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No recent calls.</div>
              ) : (() => {
                const total = drillQuery.data.recent.length;
                const safePage = clampPage(recentPage, total, recentPageSize);
                const start = (safePage - 1) * recentPageSize;
                const pageRows = drillQuery.data.recent.slice(start, start + recentPageSize);
                return (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>When</TableHead>
                            <TableHead>Feature</TableHead>
                            <TableHead>Model</TableHead>
                            <TableHead className="text-right">Tokens (in/out)</TableHead>
                            <TableHead className="text-right">Latency</TableHead>
                            <TableHead className="text-right">Cost</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pageRows.map((row) => (
                            <TableRow key={row.id} data-testid={`row-drilldown-recent-${row.id}`}>
                              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatDate(row.createdAt)}
                              </TableCell>
                              <TableCell>{row.feature}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{row.model}</TableCell>
                              <TableCell className="text-right text-xs whitespace-nowrap">
                                {formatNum(row.inputTokens)} / {formatNum(row.outputTokens)}
                              </TableCell>
                              <TableCell className="text-right text-xs text-muted-foreground">{formatNum(row.latencyMs)} ms</TableCell>
                              <TableCell className="text-right text-xs">{formatUsd(row.estimatedCostUsd)}</TableCell>
                              <TableCell>
                                {row.success
                                  ? <Badge variant="secondary">ok</Badge>
                                  : <Badge variant="destructive" title={row.errorMessage ?? undefined}>fail</Badge>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="p-3 border-t">
                      <PaginationControls
                        page={safePage}
                        pageSize={recentPageSize}
                        total={total}
                        onPageChange={setRecentPage}
                        onPageSizeChange={setRecentPageSize}
                        testIdPrefix="drilldown-recent"
                        itemLabel="calls"
                      />
                    </div>
                  </>
                );
              })()}
            </Card>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="button-close-drilldown">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
